import store from "../state";
import router from "../router";

function getApiEndpoint() {
  let apiEndpoint = process.env.VUE_APP_API_HOST || "http://localhost:8000";

  return apiEndpoint;
}

function queryString(query) {
  return query
    ? "?" +
        Object.keys(query)
          .map((key) => key + "=" + query[key])
          .join("&")
    : "";
}

async function _apiFetch({
  path,
  query = null,
  method = "GET",
  data = null,
  authenticated = false,
}) {
  let headers = {};
  if (authenticated) {
    let token = store.getters.accessToken;
    headers["Authorization"] = `Bearer ${token}`;
  }

  if (data != null) {
    headers["Content-Type"] = "application/json";
  }

  let fullPath = `${getApiEndpoint()}${path}${queryString(query)}`;

  const fetched = await fetch(fullPath, {
    method,
    headers,
    body: data ? JSON.stringify(data) : null,
  });

  return {
    ok: fetched.ok,
    data: await fetched.json(),
  };
}

// Wrapper around the apiFetch functionality that catches when the
// request fails due to an invalid authentication token, and attempts
// to refresh the token before retrying once.
async function apiFetch(args) {
  let result = await _apiFetch(args);

  if (
    args.authenticated &&
    !result.ok &&
    result.data.error.code == "INVALID_AUTHENTICATION_TOKEN"
  ) {
    if (!(await store.dispatch("refreshLogIn"))) {
      store.commit("resetStore");
      router.push("/sign-in");
    }
    result = await _apiFetch(args);
  }

  return result;
}

let api = {
  async signUp(
    email,
    password,
    handle,
    name,
    marketingEmailsEnabled,
    invitationId
  ) {
    return apiFetch({
      path: "/accounts/sign-up",
      method: "POST",
      data: {
        email,
        password,
        handle,
        name,
        marketing_emails_enabled: marketingEmailsEnabled,
        invitation_id: invitationId,
      },
    });
  },

  async getInvitation(invitationId) {
    return apiFetch({
      path: `/invitations/${invitationId}`,
      method: "GET",
    });
  },

  async verify(email, token) {
    return apiFetch({
      path: "/accounts/confirm-email",
      method: "POST",
      data: {
        email,
        token,
      },
    });
  },

  async signIn(email, password, twoFactorCode) {
    return apiFetch({
      path: "/accounts/log-in",
      method: "POST",
      data: {
        email,
        password,
        two_factor_code: twoFactorCode,
      },
    });
  },

  async addSupportMessage(subject, body, name, email) {
    return apiFetch({
      path: "/support/messages",
      method: "POST",
      data: {
        subject,
        body,
        name,
        email,
      },
      authenticated: store.getters.authenticated,
    });
  },

  async resetPassword(email) {
    return apiFetch({
      path: "/accounts/reset-password",
      method: "POST",
      data: {
        email,
      },
    });
  },

  async setPassword(email, password, token) {
    return apiFetch({
      path: "/accounts/set-password",
      method: "POST",
      data: {
        email,
        password,
        token,
      },
    });
  },

  async changePassword(password, newPassword) {
    return apiFetch({
      path: "/accounts/change-password",
      method: "POST",
      data: {
        password,
        new_password: newPassword,
      },
      authenticated: true,
    });
  },

  async enableTwoFactor() {
    return apiFetch({
      path: "/accounts/two-factor/enable",
      method: "POST",
      authenticated: true,
    });
  },

  async disableTwoFactor() {
    return apiFetch({
      path: "/accounts/two-factor/disable",
      method: "POST",
      authenticated: true,
    });
  },

  async confirmTwoFactor(code) {
    return apiFetch({
      path: "/accounts/two-factor/confirm",
      method: "POST",
      data: {
        code,
      },
      authenticated: true,
    });
  },

  async resendEmailConfirmation(email) {
    return apiFetch({
      path: "/accounts/confirm-email/resend",
      method: "POST",
      data: {
        email,
      },
    });
  },

  async createOnboardingCharge(contract) {
    return apiFetch({
      path: "/accounts/create-onboarding-invoice",
      method: "POST",
      data: {
        contract,
      },
      authenticated: true,
    });
  },

  async saveCreditCard(data) {
    return apiFetch({
      path: "/accounts/add-credit-card",
      method: "POST",
      data: {
        data,
      },
      authenticated: true,
    });
  },

  async createLinkToken(data) {
    return apiFetch({
      path: "/accounts/create-link-token",
      method: "POST",
      data: { data },
      authenticated: true,
    });
  },

  async saveBankAccount(data) {
    return apiFetch({
      path: "/accounts/save-bank-account",
      method: "POST",
      data: { data },
      authenticated: true,
    });
  },

  async refreshLogIn(refreshToken) {
    return apiFetch({
      path: "/accounts/refresh-log-in",
      method: "POST",
      data: {
        refresh_token: refreshToken,
      },
    });
  },

  async getUser() {
    return apiFetch({
      path: "/accounts/self",
      method: "GET",
      authenticated: true,
    });
  },

  async putAccount(name, handle, marketingEmailsEnabled) {
    return apiFetch({
      path: "/accounts/self",
      method: "PUT",
      authenticated: true,
      data: {
        name,
        handle,
        marketing_emails_enabled: marketingEmailsEnabled,
      },
    });
  },

  async getInvitations(fields) {
    let query = {};
    if (fields !== undefined && fields !== null && fields !== "") {
      query.fields = fields;
    }

    return apiFetch({
      path: "/invitations",
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async acceptInvitation(invitationId) {
    return apiFetch({
      path: `/invitations/${invitationId}/accept`,
      method: "POST",
      authenticated: true,
    });
  },

  async rejectInvitation(invitationId) {
    return apiFetch({
      path: `/invitations/${invitationId}/reject`,
      method: "POST",
      authenticated: true,
    });
  },

  async getTeams() {
    return apiFetch({
      path: `/teams`,
      method: "GET",
      authenticated: true,
    });
  },

  async getAllTeamsForContracts() {
    return apiFetch({
      path: `/contracts/allTeams`,
      method: "GET",
      authenticated: true,
    });
  },

  async getTeamMembers(teamId, fields) {
    let query = {};
    if (fields !== undefined && fields !== null && fields !== "") {
      query.fields = fields;
    }
    return apiFetch({
      path: `/teams/${teamId}/members`,
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async getPermissions() {
    return apiFetch({
      path: `/permissions`,
      method: "GET",
      authenticated: true,
    });
  },

  async getAccountRoles() {
    return apiFetch({
      path: `/accounts/self/roles`,
      method: "GET",
      authenticated: true,
    });
  },

  async getRoles(teamId, fields) {
    let query = {};
    if (fields !== undefined && fields !== null && fields !== "") {
      query.fields = fields;
    }
    return apiFetch({
      path: `/teams/${teamId}/roles`,
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async getRole(roleId) {
    return apiFetch({
      path: `/roles/${roleId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async getTeamInvitations(teamId) {
    return apiFetch({
      path: `/teams/${teamId}/invitations`,
      method: "GET",
      authenticated: true,
    });
  },

  async postTeam(name, handle) {
    return apiFetch({
      path: "/teams",
      method: "POST",
      authenticated: true,
      data: {
        name,
        handle,
      },
    });
  },

  async putTeam(teamId, name, handle) {
    return apiFetch({
      path: `/teams/${teamId}`,
      method: "PUT",
      authenticated: true,
      data: {
        teamId,
        name,
        handle,
      },
    });
  },

  async postTeamInvitations(teamId, roleId, email) {
    return apiFetch({
      path: `/teams/${teamId}/invitations`,
      method: "POST",
      authenticated: true,
      data: {
        invitations: [
          {
            role_id: roleId,
            email: email,
          },
        ],
      },
    });
  },

  async getContractFacilities() {
    return apiFetch({
      path: `/contract-facilities`,
      method: "GET",
      authenticated: true,
    });
  },

  async getContractFacility(contractFacilityId) {
    return apiFetch({
      path: `/contract-facilities/${contractFacilityId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async deleteContractFacility(contractFacilityId) {
    return apiFetch({
      path: `/contract-facilities/${contractFacilityId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async postContractFacility(name, contact_email, handle) {
    return apiFetch({
      path: `/contract-facilities/create`,
      method: "POST",
      authenticated: true,
      data: {
        name,
        contact_email,
        handle,
      },
    });
  },

  async putContractFacility(contractFacilityId, name, contact_email) {
    return apiFetch({
      path: `/contract-facilities/${contractFacilityId}`,
      method: "PUT",
      authenticated: true,
      data: {
        name,
        contact_email,
      },
    });
  },

  async getFacilities(teamId) {
    return apiFetch({
      path: `/teams/${teamId}/facilities`,
      method: "GET",
      authenticated: true,
    });
  },

  async getFacility(teamId, facilityId) {
    return apiFetch({
      path: `/facilities/${facilityId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async getFacilityModules(teamId, facilityId) {
    return apiFetch({
      path: `/facilities/${facilityId}/modules`,
      method: "GET",
      authenticated: true,
    });
  },

  async updateFacility(
    teamId,
    module_type,
    module_id,
    min_wattage,
    max_wattage,
    target_wattage,
    display_order,
    notes
  ) {
    return apiFetch({
      path: `/facilities/modules/${module_type}/${module_id}`,
      method: "PUT",
      authenticated: true,
      data: {
        min_wattage,
        max_wattage,
        target_wattage,
        display_order,
        notes,
      },
    });
  },

  async getGroups(teamId) {
    return apiFetch({
      path: `/teams/${teamId}/groups?fields=id,name`,
      method: "GET",
      authenticated: true,
    });
  },

  async postGroup(teamId, name, handle, notes, workerIds) {
    return apiFetch({
      path: `/teams/${teamId}/groups`,
      method: "POST",
      authenticated: true,
      data: {
        name,
        handle,
        notes,
        worker_ids: workerIds,
      },
    });
  },

  async getGroup(teamId, groupId) {
    return apiFetch({
      path: `/groups/${groupId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async getGroupStatistics(teamId, groupId) {
    let path;
    if (groupId === "default") {
      path = `/teams/${teamId}/workers/statistics`;
    } else {
      path = `/groups/${groupId}/workers/statistics`;
    }

    return apiFetch({
      path,
      method: "GET",
      authenticated: true,
    });
  },

  async getGroupStatisticsMulti(groupIds) {
    return apiFetch({
      path: `/groups/statistics_multi`,
      method: "POST",
      authenticated: true,
      data: {
        group_ids: groupIds,
      },
    });
  },

  async getGroupShares(teamId, groupId) {
    let path;
    if (groupId === "default") {
      path = `/teams/${teamId}/workers/share-datav2`;
    } else {
      path = `/groups/${groupId}/workers/share-datav2`;
    }

    return apiFetch({
      path,
      method: "GET",
      authenticated: true,
    });
  },

  async getGroupSharesMulti(groupIds) {
    return apiFetch({
      path: `/groups/share-datav2_multi`,
      method: "POST",
      authenticated: true,
      data: {
        group_ids: groupIds,
      },
    });
  },

  async putGroup(teamId, groupId, name, handle, notes, workerIds) {
    return apiFetch({
      path: `/groups/${groupId}`,
      method: "PUT",
      authenticated: true,
      data: {
        name,
        handle,
        notes,
        worker_ids: workerIds,
      },
    });
  },

  async getWorkers(teamId, limit, offset, searchString, fields) {
    let query = {};
    if (limit) {
      query.limit = limit;
    }

    if (offset) {
      query.offset = offset;
    }

    if (
      searchString !== undefined &&
      searchString !== null &&
      searchString !== ""
    ) {
      query.name__contains = searchString;
    }

    if (fields !== undefined && fields !== null && fields !== "") {
      query.fields = fields;
    }

    return apiFetch({
      path: `/teams/${teamId}/workers`,
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async getWorkersForTeam(contractId, teamId) {
    return apiFetch({
      path: `/contracts/${contractId}/teams/${teamId}/workers`,
      method: "GET",
      authenticated: true,
    });
  },

  async getTransferWorkersForTeam(teamId) {
    return apiFetch({
      path: `/contracts/transfers/teams/${teamId}/workers`,
      method: "GET",
      authenticated: true,
    });
  },

  async getWorkerCount(teamId) {
    return apiFetch({
      path: `/teams/${teamId}/workers/statistics/count`,
      method: "GET",
      authenticated: true,
    });
  },

  async postWorker(
    teamId,
    name,
    deviceType,
    password,
    poolWorkerName,
    poolWorkerPassword,
    poolId,
    handle,
    serial_number,
    notes
  ) {
    return apiFetch({
      path: `/teams/${teamId}/workers`,
      method: "POST",
      authenticated: true,
      data: {
        name,
        handle,
        serial_number: serial_number,
        notes: notes,
        device_id: deviceType,
        password,
        pool_configuration: {
          worker_name: poolWorkerName,
          password: poolWorkerPassword,
          pool_id: poolId,
        },
      },
    });
  },

  async postWorkers(teamId, workers) {
    return apiFetch({
      path: `/teams/${teamId}/workers?bulk`,
      method: "POST",
      authenticated: true,
      data: {
        workers,
      },
    });
  },

  async postGroupWorkers(groupId, workers) {
    return apiFetch({
      path: `/groups/${groupId}/workers?bulk`,
      method: "POST",
      authenticated: true,
      data: {
        workers,
      },
    });
  },

  async getWorker(teamId, workerId) {
    return apiFetch({
      path: `/workers/${workerId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async deleteWorker(workerId) {
    return apiFetch({
      path: `/workers/${workerId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async deleteGroup(groupId) {
    return apiFetch({
      path: `/groups/${groupId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async getWorkerHashrate(teamId, workerId) {
    return apiFetch({
      path: `/workers/${workerId}/statistics/hashrate`,
      method: "GET",
      authenticated: true,
    });
  },

  async getWorkerLastSubmission(teamId, workerId) {
    return apiFetch({
      path: `/workers/${workerId}/statistics/last-submission`,
      method: "GET",
      authenticated: true,
    });
  },

  async getWorkerShares(teamId, workerId, resolution) {
    return apiFetch({
      path: `/workers/${workerId}/share-datav2`,
      method: "GET",
      authenticated: true,
      query: { resolution },
    });
  },

  async putWorker(
    teamId,
    workerId,
    name,
    deviceType,
    password,
    poolWorkerName,
    poolWorkerPassword,
    poolId,
    handle,
    serial_number,
    notes
  ) {
    return apiFetch({
      path: `/workers/${workerId}`,
      method: "PUT",
      authenticated: true,
      data: {
        device_id: deviceType,
        name,
        handle,
        serial_number: serial_number,
        notes: notes,
        password,
        pool_configuration: {
          pool_id: poolId,
          worker_name: poolWorkerName,
          password: poolWorkerPassword,
        },
      },
    });
  },

  async getDeviceTypes() {
    return apiFetch({
      path: "/devices",
      method: "GET",
      authenticated: true,
    });
  },

  async getPools() {
    return apiFetch({
      path: "/pools",
      method: "GET",
      authenticated: true,
    });
  },

  async getInactiveWorkers(
    groupId,
    teamId,
    limit,
    submitted,
    offset,
    inactive
  ) {
    let path;
    // This code operates on the presumption that only two kinds of invariant cases are passed in:
    // Either groupId is set and teamId isn't, or vice versa.
    if (groupId !== null) {
      path = `/groups/${groupId}/workers`;
    } else {
      // TODO/FIXME: This code will throw an error if teamId is set to null.
      path = `/teams/${teamId}/workers`;
    }
    let query = {
      limit,
      offset,
      order_by: "last_submitted__descending",
    };
    if (submitted) {
      query["submitted"] = true;
    }

    if (inactive) {
      query["inactive"] = true;
    }

    return apiFetch({
      path,
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async getAlerts(teamId) {
    return apiFetch({
      path: `/teams/${teamId}/alerts`,
      method: "GET",
      authenticated: true,
    });
  },

  async getAlertEvents(
    teamId,
    groupId,
    alertId,
    limit,
    offset,
    format,
    timezone
  ) {
    let path;
    if (alertId) {
      path = `/alerts/${alertId}/events`;
    } else if (teamId) {
      path = `/teams/${teamId}/alerts/events`;
    } else {
      path = `/groups/${groupId}/alerts/events`;
    }

    let query;
    if (format === "heatmap") {
      query = { format: "heatmap", timezone };
    } else {
      query = { limit, offset };
    }

    return apiFetch({
      path,
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async getAlert(alertId) {
    return apiFetch({
      path: `/alerts/${alertId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async deleteAlert(alertId) {
    return apiFetch({
      path: `/alerts/${alertId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async getContracts(limit, offset, searchString) {
    let query = {};
    if (limit) {
      query.limit = limit;
    }

    if (offset) {
      query.offset = offset;
    }

    if (
      searchString !== undefined &&
      searchString !== null &&
      searchString !== ""
    ) {
      query.name__contains = searchString;
    }

    return apiFetch({
      path: `/contracts`,
      method: "GET",
      authenticated: true,
      query,
    });
  },

  async onboardClient(
    template_id,
    signer_name,
    signer_email,
    team_id,
    team_name,
    batch_number,
    model,
    kwh_rate,
    term,
    quantity,
    setup_fee_per_miner,
    security_deposit_per_miner,
    insurance_per_miner,
    wattage,
    serials,
    shipping_provider,
    tracking_number,
    workers,
    start_date,
    expiration,
    ccEmailOne,
    ccEmailTwo,
    metadata
  ) {
    return apiFetch({
      path: `/onboarding`,
      method: "POST",
      authenticated: true,
      data: {
        template_id,
        signer_name,
        signer_email,
        team_id,
        team_name,
        batch_number,
        model,
        kwh_rate,
        term,
        quantity,
        setup_fee_per_miner,
        security_deposit_per_miner,
        insurance_per_miner,
        wattage,
        serials,
        shipping_provider,
        tracking_number,
        workers,
        start_date,
        expiration,
        ccEmailOne,
        ccEmailTwo,
        metadata,
      },
    });
  },

  async postContract(
    teamId,
    facilityId,
    batch_number,
    rate_per_kWh,
    machine_setup_fee,
    machine_security_deposit,
    machine_insurance,
    term_length,
    machine_count,
    customer_type,
    device_type_id,
    compatible,
    hourly_watt_consumption,
    shipped_or_transferred,
    selectedWorkerIds,
    invoiced_in_stripe,
    expiration,
    start_date
  ) {
    return apiFetch({
      path: `/contracts/create-new-contract`,
      method: "POST",
      authenticated: true,
      data: {
        teamId,
        facilityId,
        batch_number,
        rate_per_kWh,
        machine_setup_fee,
        machine_security_deposit,
        machine_insurance,
        term_length,
        machine_count,
        customer_type,
        device_type_id,
        compatible,
        hourly_watt_consumption,
        shipped_or_transferred,
        selectedWorkerIds,
        invoiced_in_stripe,
        expiration,
        start_date,
      },
    });
  },

  async putContract(
    contractId,
    teamId,
    facilityId,
    batch_number,
    rate_per_kWh,
    machine_setup_fee,
    machine_security_deposit,
    machine_insurance,
    term_length,
    machine_count,
    customer_type,
    device_type_id,
    compatible,
    hourly_watt_consumption,
    onboarding_invoice_created,
    payment_method_ingested,
    pool_info_captured,
    shipped_or_transferred,
    selectedWorkerIds,
    invoiced_in_stripe,
    expiration,
    start_date
  ) {
    return apiFetch({
      path: `/contracts/${contractId}`,
      method: "PUT",
      authenticated: true,
      data: {
        teamId,
        facilityId,
        batch_number,
        rate_per_kWh,
        machine_setup_fee,
        machine_security_deposit,
        machine_insurance,
        term_length,
        machine_count,
        customer_type,
        device_type_id,
        compatible,
        hourly_watt_consumption,
        onboarding_invoice_created,
        payment_method_ingested,
        pool_info_captured,
        shipped_or_transferred,
        selectedWorkerIds,
        invoiced_in_stripe,
        expiration,
        start_date,
      },
    });
  },

  async putContractAsOnboardingUser(
    teamId,
    contractId,
    onboarding_invoice_created,
    payment_method_ingested,
    pool_info_captured
  ) {
    return apiFetch({
      path: `/onboarding/contract-edit/${contractId}`,
      method: "PUT",
      data: {
        teamId,
        onboarding_invoice_created,
        payment_method_ingested,
        pool_info_captured,
      },
      authenticated: true,
    });
  },

  async getContract(contractId) {
    return apiFetch({
      path: `/contracts/${contractId}`,
      method: "GET",
      authenticated: true,
    });
  },

  async deleteContract(contractId) {
    return apiFetch({
      path: `/contracts/${contractId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async deleteRole(roleId) {
    return apiFetch({
      path: `/roles/${roleId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async removeTeamMember(teamId, memberId) {
    return apiFetch({
      path: `/teams/${teamId}/members/${memberId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async putTeamMember(teamId, memberId, roleId) {
    return apiFetch({
      path: `/teams/${teamId}/members/${memberId}`,
      method: "PUT",
      authenticated: true,
      data: {
        role_id: roleId,
      },
    });
  },

  async deleteTeam(teamId) {
    return apiFetch({
      path: `/teams/${teamId}`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async leaveTeam(teamId) {
    return apiFetch({
      path: `/teams/${teamId}/members/self`,
      method: "DELETE",
      authenticated: true,
    });
  },

  async postAlert(
    name,
    kind,
    handle,
    groupId,
    parameters,
    accountIds,
    notes,
    enabled,
    notifyOnResolve,
    notifyRepeatDelay,
    timeThreshold,
    notifyOnChange,
    hashrateThreshold
  ) {
    return apiFetch({
      path: `/groups/${groupId}/alerts`,
      method: "POST",
      authenticated: true,
      data: {
        name,
        kind,
        handle,
        notes,
        parameters,
        account_ids: accountIds,
        enabled,
        notify_on_resolve: notifyOnResolve,
        notify_repeat_delay: notifyRepeatDelay,
        time_threshold: timeThreshold,
        notify_on_change: notifyOnChange,
        hashrate_threshold: hashrateThreshold,
      },
    });
  },

  async enableAlert(alertId) {
    return apiFetch({
      path: `/alerts/${alertId}/enable`,
      method: "POST",
      authenticated: true,
    });
  },

  async disableAlert(alertId) {
    return apiFetch({
      path: `/alerts/${alertId}/disable`,
      method: "POST",
      authenticated: true,
    });
  },

  async putAlert(
    alertId,
    name,
    kind,
    handle,
    groupId,
    parameters,
    accountIds,
    notes,
    enabled,
    notifyOnResolve,
    notifyRepeatDelay,
    timeThreshold,
    notifyOnChange,
    hashrateThreshold
  ) {
    return apiFetch({
      path: `/alerts/${alertId}`,
      method: "PUT",
      authenticated: true,
      data: {
        name,
        kind,
        handle,
        group_id: groupId,
        parameters,
        account_ids: accountIds,
        notes,
        enabled,
        notify_on_resolve: notifyOnResolve,
        notify_repeat_delay: notifyRepeatDelay,
        time_threshold: timeThreshold,
        notify_on_change: notifyOnChange,
        hashrate_threshold: hashrateThreshold,
      },
    });
  },

  async putRole(
    roleId,
    name,
    handle,
    description,
    limitedGroupAccess,
    permissionIds,
    accessibleGroupIds
  ) {
    return apiFetch({
      path: `/roles/${roleId}`,
      method: "PUT",
      authenticated: true,
      data: {
        name,
        handle,
        description,
        limited_group_access: limitedGroupAccess,
        permission_ids: permissionIds,
        accessible_group_ids: accessibleGroupIds,
      },
    });
  },

  async postRole(
    teamId,
    name,
    handle,
    description,
    limitedGroupAccess,
    permissionIds,
    accessibleGroupIds
  ) {
    return apiFetch({
      path: `/teams/${teamId}/roles`,
      method: "POST",
      authenticated: true,
      data: {
        name,
        handle,
        description,
        limited_group_access: limitedGroupAccess,
        permission_ids: permissionIds,
        accessible_group_ids: accessibleGroupIds,
      },
    });
  },

  async getInvoices(activeTeamId) {
    return apiFetch({
      path: `/billing/${activeTeamId}/get-past-invoices`,
      method: "GET",
      authenticated: true,
    });
  },

  async getDefaultPaymentMethod(activeTeamId) {
    return apiFetch({
      path: `/billing/${activeTeamId}/get-payment-method`,
      method: "GET",
      authenticated: true,
    });
  },

  async getTeamContracts(activeTeamId) {
    return apiFetch({
      path: `/contracts/${activeTeamId}/get-team-contracts`,
      method: "GET",
      authenticated: true,
    });
  },

  async getAllPaymentMethods(activeTeamId) {
    return apiFetch({
      path: `/billing/${activeTeamId}/change-default-payment-method`,
      method: "GET",
      authenticated: true,
    });
  },

  async changeDefaultPaymentMethod(activeTeamId, pm) {
    return apiFetch({
      path: `/billing/${activeTeamId}/change-default-payment-method`,
      method: "POST",
      data: {
        pm: pm,
      },
      authenticated: true,
    });
  },

  async setTeamPoolSettings(activeTeamId, poolId, poolAccount, device_type) {
    return apiFetch({
      path: `/teams/${activeTeamId}/pool-config`,
      method: "POST",
      data: {
        poolId: poolId,
        poolAccount: poolAccount,
        device_type: device_type,
      },
      authenticated: true,
    });
  },
  async getHelloSignTemplates() {
    return apiFetch({
      path: "/onboarding/get-templates",
      method: "GET",
      authenticated: true,
    });
  },

  // Reactor admin proxy (Phase 3 of ADMIN_UI_Plan.md)
  async reactorAdminGetCapacity() {
    return apiFetch({
      path: "/reactor-admin/capacity",
      method: "GET",
      authenticated: true,
    });
  },
  async reactorAdminPatchCapacity(body) {
    return apiFetch({
      path: "/reactor-admin/capacity",
      method: "PUT",
      data: body,
      authenticated: true,
    });
  },
  async reactorAdminListContracts(status) {
    return apiFetch({
      path: "/reactor-admin/contracts",
      method: "GET",
      query: status ? { status } : null,
      authenticated: true,
    });
  },
  async reactorAdminCreateContract(body) {
    return apiFetch({
      path: "/reactor-admin/contracts",
      method: "POST",
      data: body,
      authenticated: true,
    });
  },
  async reactorAdminCancelContract(contractId, instant) {
    return apiFetch({
      path: `/reactor-admin/contracts/${contractId}/cancel`,
      method: "POST",
      data: { instant: !!instant },
      authenticated: true,
    });
  },
};

export default api;
